import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import ProfileHeader from './components/ProfileHeader';
import PersonalInfoTab from './components/PersonalInfoTab';
import ProfessionalInfoTab from './components/ProfessionalInfoTab';
import SecurityTab from './components/SecurityTab';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const UserProfileManagement = () => {
  const [userRole, setUserRole] = useState('contractor');
  const [activeTab, setActiveTab] = useState('personal');
  const [isSaving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const mockProfiles = {
    contractor: {
      name: 'Jan de Vries',
      email: 'jan.devries@email.nl',
      phone: '+31 6 12345678',
      location: 'Amsterdam, Nederland',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_113acd116-1763292512957.png",
      avatarAlt: 'Professional headshot of Dutch man with short brown hair wearing blue work shirt and safety vest',
      completeness: 85,
      firstName: 'Jan',
      lastName: 'de Vries',
      birthDate: '1985-03-15',
      gender: 'male',
      street: 'Hoofdstraat 123',
      postalCode: '1012 AB',
      city: 'Amsterdam',
      country: 'nl',
      experienceLevel: 'senior',
      availability: ['full_time', 'weekends'],
      hourlyRate: '45.00',
      travelDistance: '50',
      skills: [
      { value: 'electrical', label: 'Elektriciteit' },
      { value: 'hvac', label: 'HVAC Systemen' },
      { value: 'solar', label: 'Zonnepanelen' }],

      certifications: [
      {
        name: 'NEN 3140 Elektrotechnisch Installateur',
        issuer: 'UNETO-VNI',
        expiryDate: '31-12-2026',
        number: 'NEN-2024-12345'
      },
      {
        name: 'F-gassen Certificaat Categorie I',
        issuer: 'STEK',
        expiryDate: '15-06-2027',
        number: 'FG-2023-67890'
      }],

      portfolio: [
      {
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_15de69241-1764993586968.png",
        imageAlt: 'Modern residential solar panel installation on red tile roof with blue sky background'
      },
      {
        image: "https://images.unsplash.com/photo-1644944200048-40f8b38fa62e",
        imageAlt: 'Professional electrician installing white wall-mounted air conditioning unit in modern living room'
      },
      {
        image: "https://images.unsplash.com/photo-1658505988260-cfdba6b5ad83",
        imageAlt: 'Close-up of hands connecting electrical wires in modern circuit breaker panel'
      }],

      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: true,
      lastVerification: '14-01-2026 om 20:30',
      profileVisible: true,
      emailNotifications: true,
      shareWithAffiliates: true,
      marketingConsent: false
    },
    client: {
      name: 'Maria van den Berg',
      email: 'maria@techbouw.nl',
      phone: '+31 20 1234567',
      location: 'Rotterdam, Nederland',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1577de600-1763300537455.png",
      avatarAlt: 'Professional businesswoman with long brown hair wearing navy blue blazer in modern office setting',
      completeness: 92,
      firstName: 'Maria',
      lastName: 'van den Berg',
      birthDate: '1978-07-22',
      gender: 'female',
      street: 'Weena 505',
      postalCode: '3013 AL',
      city: 'Rotterdam',
      country: 'nl',
      kvkNumber: '12345678',
      companyName: 'TechBouw BV',
      vatNumber: 'NL123456789B01',
      website: 'https://www.techbouw.nl',
      serviceTypes: ['commercial', 'industrial'],
      employeeCount: '45',
      companyDescription: 'Gespecialiseerd in technische installaties voor commerciële en industriële projecten',
      billingAddress: 'Weena 505, 3013 AL Rotterdam',
      iban: 'NL91 ABNA 0417 1643 00',
      paymentTerms: '30',
      poPrefix: 'TB-',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: false,
      lastVerification: '',
      profileVisible: true,
      emailNotifications: true,
      shareWithAffiliates: true,
      marketingConsent: true
    },
    affiliate: {
      name: 'Pieter Jansen',
      email: 'pieter@techpartners.nl',
      phone: '+31 30 7654321',
      location: 'Utrecht, Nederland',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f452bc55-1763293392876.png",
      avatarAlt: 'Professional businessman with short blonde hair wearing gray suit and blue tie in corporate office',
      completeness: 78,
      firstName: 'Pieter',
      lastName: 'Jansen',
      birthDate: '1982-11-08',
      gender: 'male',
      street: 'Croeselaan 18',
      postalCode: '3521 CB',
      city: 'Utrecht',
      country: 'nl',
      kvkNumber: '87654321',
      networkType: 'recruitment',
      activeContacts: '125',
      specialization: 'HVAC en Elektrotechnische professionals',
      autoPayoutEnabled: true,
      payoutThreshold: '250.00',
      payoutIban: 'NL91 RABO 0123 4567 89',
      referralLink: 'https://techinstallhub.nl/ref/pieter-jansen',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: true,
      lastVerification: '14-01-2026 om 19:15',
      profileVisible: true,
      emailNotifications: true,
      shareWithAffiliates: false,
      marketingConsent: true
    },
    superadmin: {
      name: 'Admin Beheerder',
      email: 'admin@techinstallhub.nl',
      phone: '+31 20 9876543',
      location: 'Amsterdam, Nederland',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12ac98e01-1763298613824.png",
      avatarAlt: 'Professional administrator with short dark hair wearing black suit and glasses in modern tech office',
      completeness: 100,
      firstName: 'Admin',
      lastName: 'Beheerder',
      birthDate: '1975-05-12',
      gender: 'male',
      street: 'Zuidas 1',
      postalCode: '1077 XX',
      city: 'Amsterdam',
      country: 'nl',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: true,
      lastVerification: '14-01-2026 om 21:00',
      profileVisible: false,
      emailNotifications: true,
      shareWithAffiliates: false,
      marketingConsent: false
    }
  };

  const [profileData, setProfileData] = useState(mockProfiles?.[userRole]);
  const [formData, setFormData] = useState(mockProfiles?.[userRole]);

  useEffect(() => {
    const newProfile = mockProfiles?.[userRole];
    setProfileData(newProfile);
    setFormData(newProfile);
  }, [userRole]);

  const tabs = [
  { id: 'personal', label: 'Persoonlijke Gegevens', icon: 'User' },
  { id: 'professional', label: 'Professionele Informatie', icon: 'Briefcase' },
  { id: 'security', label: 'Beveiligingsinstellingen', icon: 'Lock' }];


  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setProfileData(formData);
      setSaving(false);
      alert('Wijzigingen succesvol opgeslagen!');
    }, 1500);
  };

  const handleEditPhoto = () => {
    alert('Profielfoto wijzigen functionaliteit');
  };

  const handleAddSkill = () => {
    alert('Vaardigheid toevoegen functionaliteit');
  };

  const handleRemoveSkill = (index) => {
    const newSkills = formData?.skills?.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      skills: newSkills
    }));
  };

  const handleAddCertification = () => {
    alert('Certificering toevoegen functionaliteit');
  };

  const handleRemoveCertification = (index) => {
    const newCertifications = formData?.certifications?.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      certifications: newCertifications
    }));
  };

  const handleUploadPortfolio = () => {
    alert('Portfolio foto uploaden functionaliteit');
  };

  const handleEnable2FA = () => {
    alert('2FA inschakelen functionaliteit - QR code wordt getoond');
  };

  const handleDisable2FA = () => {
    if (confirm('Weet u zeker dat u twee-factor authenticatie wilt uitschakelen?')) {
      setFormData((prev) => ({
        ...prev,
        twoFactorEnabled: false
      }));
    }
  };

  return (
    <MainLayout userRole={userRole} userName={profileData?.name}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            variant={userRole === 'contractor' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserRole('contractor')}>

            Installateur
          </Button>
          <Button
            variant={userRole === 'client' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserRole('client')}>

            Opdrachtgever
          </Button>
          <Button
            variant={userRole === 'affiliate' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserRole('affiliate')}>

            Affiliate
          </Button>
          <Button
            variant={userRole === 'superadmin' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setUserRole('superadmin')}>

            Super Admin
          </Button>
        </div>

        <ProfileHeader
          userRole={userRole}
          profileData={profileData}
          onEditPhoto={handleEditPhoto} />


        <div
          className="rounded-lg overflow-hidden"
          style={{
            background: '#0D0D0D',
            border: '1px solid #1A1A1A'
          }}>
          
          <div className="border-b border-border overflow-x-auto">
            <div className="flex min-w-max">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => handleTabChange(tab?.id)}
                className={`
                    flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium text-sm md:text-base
                    transition-smooth border-b-2 flex-shrink-0
                    ${activeTab === tab?.id ?
                'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'}
                  `
                }>

                  <Icon name={tab?.icon} size={18} />
                  <span className="whitespace-nowrap">{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8">
            {activeTab === 'personal' &&
            <PersonalInfoTab
              userRole={userRole}
              formData={formData}
              errors={errors}
              onChange={handleFormChange}
              onSave={handleSave}
              isSaving={isSaving} />

            }

            {activeTab === 'professional' &&
            <ProfessionalInfoTab
              userRole={userRole}
              formData={formData}
              onChange={handleFormChange}
              onSave={handleSave}
              isSaving={isSaving}
              onAddSkill={handleAddSkill}
              onRemoveSkill={handleRemoveSkill}
              onAddCertification={handleAddCertification}
              onRemoveCertification={handleRemoveCertification}
              onUploadPortfolio={handleUploadPortfolio} />

            }

            {activeTab === 'security' &&
            <SecurityTab
              formData={formData}
              onChange={handleFormChange}
              onSave={handleSave}
              isSaving={isSaving}
              onEnable2FA={handleEnable2FA}
              onDisable2FA={handleDisable2FA} />

            }
          </div>
        </div>
      </div>
    </MainLayout>);

};

export default UserProfileManagement;